/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { IInspectorControl_Any, TInspectorControlProps } from "../InspectorControlTypes";
import { ISchemaConstAnyModel, SCHEMA_CONST_ANY_VALUE_TYPE } from "@adapptio/lib-blueprint";
import {
	FieldGroup,
	FieldModeButton,
	IFieldModeButtonProps,
	TSelectOption
} from "@adapptio/lib-editor-ui";
import { isUndefined } from "@adapptio/lib-shared";

type TControlAnyAllowedTypes = Record<SCHEMA_CONST_ANY_VALUE_TYPE, boolean>;

function getAllowedTypes(model: ISchemaConstAnyModel): TControlAnyAllowedTypes {
	const schemaAllowedTypes = model.schema.opts.allowedTypes;

	return isUndefined(schemaAllowedTypes)
		? {
				[SCHEMA_CONST_ANY_VALUE_TYPE.ARRAY]: true,
				[SCHEMA_CONST_ANY_VALUE_TYPE.BOOLEAN]: true,
				[SCHEMA_CONST_ANY_VALUE_TYPE.DATE]: true,
				[SCHEMA_CONST_ANY_VALUE_TYPE.MAP]: true,
				[SCHEMA_CONST_ANY_VALUE_TYPE.NUMBER]: true,
				[SCHEMA_CONST_ANY_VALUE_TYPE.PASSWORD]: true,
				[SCHEMA_CONST_ANY_VALUE_TYPE.STRING]: true
		  }
		: {
				[SCHEMA_CONST_ANY_VALUE_TYPE.ARRAY]:
					schemaAllowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.ARRAY] || false,
				[SCHEMA_CONST_ANY_VALUE_TYPE.BOOLEAN]:
					schemaAllowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.BOOLEAN] || false,
				[SCHEMA_CONST_ANY_VALUE_TYPE.DATE]:
					schemaAllowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.DATE] || false,
				[SCHEMA_CONST_ANY_VALUE_TYPE.MAP]:
					schemaAllowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.MAP] || false,
				[SCHEMA_CONST_ANY_VALUE_TYPE.NUMBER]:
					schemaAllowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.NUMBER] || false,
				[SCHEMA_CONST_ANY_VALUE_TYPE.PASSWORD]:
					schemaAllowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.PASSWORD] || false,
				[SCHEMA_CONST_ANY_VALUE_TYPE.STRING]:
					schemaAllowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.STRING] || false
		  };
}

function getOptionsForAllowedTypes(allowedTypes: TControlAnyAllowedTypes): TSelectOption[] {
	const options: TSelectOption[] = [];

	if (allowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.ARRAY]) {
		options.push({
			value: SCHEMA_CONST_ANY_VALUE_TYPE.ARRAY,
			icon: "mdi/code-brackets",
			label: "Array"
		});
	}

	if (allowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.BOOLEAN]) {
		options.push({
			value: SCHEMA_CONST_ANY_VALUE_TYPE.BOOLEAN,
			icon: "mdi/checkbox-marked-outline",
			label: "Boolean"
		});
	}

	if (allowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.MAP]) {
		options.push({
			value: SCHEMA_CONST_ANY_VALUE_TYPE.MAP,
			icon: "mdi/code-braces",
			label: "Map"
		});
	}

	if (allowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.NUMBER]) {
		options.push({
			value: SCHEMA_CONST_ANY_VALUE_TYPE.NUMBER,
			icon: "mdi/numeric",
			label: "Number"
		});
	}

	if (allowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.STRING]) {
		options.push({
			value: SCHEMA_CONST_ANY_VALUE_TYPE.STRING,
			icon: "mdi/text-short",
			label: "String"
		});
	}

	if (allowedTypes[SCHEMA_CONST_ANY_VALUE_TYPE.PASSWORD]) {
		options.push({
			value: SCHEMA_CONST_ANY_VALUE_TYPE.PASSWORD,
			icon: "mdi/lock",
			label: "Password"
		});
	}

	return options;
}

export interface IInspectorControlAnyProps extends TInspectorControlProps<IInspectorControl_Any> {}

export const InspectorControlAny: React.FC<IInspectorControlAnyProps> = ({ model }) => {
	const allowedTypes = getAllowedTypes(model);
	const options = getOptionsForAllowedTypes(allowedTypes);

	const getType = useCallback<() => SCHEMA_CONST_ANY_VALUE_TYPE>(() => model.valueType, [ model ]);

	const setType = useCallback<(type: SCHEMA_CONST_ANY_VALUE_TYPE) => void>(
		(type) => {
			model.schema.setType(model, type, undefined, true);
		},
		[ model ]
	);

	const handleTypeChange = useCallback<IFieldModeButtonProps["onChange"]>(
		(selectedValue) => {
			setType(selectedValue as SCHEMA_CONST_ANY_VALUE_TYPE);
		},
		[ setType ]
	);

	return (
		<FieldGroup preventTruncate>
			<FieldModeButton
				preventCompactMode
				options={options}
				value={getType()}
				onChange={handleTypeChange}
			/>
		</FieldGroup>
	);
};
